import { useQuery, type QueryKey } from 'react-query';

export function spaceMemoryCountQueryKey(spaceId: string): QueryKey {
  return ['space-memory-count', spaceId];
}

export type SpaceMemoryCountStatus = 'ready' | 'no_space';

/**
 * Canonical space-scoped memory total (Redis + auto-sync, same fallback as redisAnalytics).
 * When spaceId is missing, returns count 0 and does not call the account-wide API.
 */
export function useSpaceMemoryCount(spaceId: string | null | undefined) {
  const sid =
    typeof spaceId === 'string' && spaceId.trim().length > 0 ? spaceId.trim() : null;

  const query = useQuery(
    sid ? spaceMemoryCountQueryKey(sid) : ['space-memory-count', '__none__'],
    async () => {
      const { redisAnalyticsService } = await import('../services/redisAnalytics');
      return redisAnalyticsService.getMemoryCountWithAutoSync(sid!);
    },
    {
      enabled: !!sid,
      staleTime: 15_000,
    }
  );

  return {
    count: sid ? (query.data?.total_memory_count ?? 0) : 0,
    status: (sid ? 'ready' : 'no_space') as SpaceMemoryCountStatus,
    isLoading: Boolean(sid && query.isLoading),
    isFetching: query.isFetching,
    refetch: query.refetch,
    source: query.data?.source,
  };
}
